import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy, RouterModule } from '@angular/router';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { ComponentsModule } from './components/components.module';
import { TranslateModule } from '@ngx-translate/core';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

import { SqliteStorageService } from './services/sqlite-storage/sqlite-storage.service';
import { AuthService } from './services/auth/auth.service';
import { ErrorInterceptor } from './interceptors/error/error-interceptor';
import { CookieService } from 'ngx-cookie-service';
import { NgcCookieConsentConfig, NgcCookieConsentModule } from 'ngx-cookieconsent';
import { environment } from 'src/environments/environment';
import { GlobalErrorHandler } from './error-handlers/global-error-handler/global-error-handler.service';
import { CoreService } from './services/core/core.service';
import { ErrorLoggingService } from './error-handlers/error-logging/error-logging.service';
import { RefreshInterceptor } from './interceptors/refresh/refresh-interceptor';
import { AuthInterceptor } from './interceptors/auth/auth-interceptor';

export function initFactory(
  sqliteService: SqliteStorageService,
  authService: AuthService,
  coreService: CoreService,
  errorlogger: ErrorLoggingService
) {
  return async () => {
    await sqliteService.initializeDatabase();
    await authService.processCurrentUserData();
    await coreService.fetchOsVersion();
    await errorlogger.initializeErrorLogging();
  };
}

const cookieConfig: NgcCookieConsentConfig = {
  cookie: {
    domain: environment.cookieDomain, // it is recommended to set your domain, for cookies to work properly
    secure: true
  },
  position: "top",
  // palette: {
  //   popup: {
  //     background: "#F1F1F1",
  //   },
  // },
  theme: "classic",
  type: "opt-in",
  layout: "my-custom-layout",
  layouts: {
    "my-custom-layout": "{{messagelink}}{{buttons}}",
  },
  elements: {
    messagelink: `
    <span id="cookieconsent:desc" class="cc-message">
    <span id="cmsg">{{message}}</span>
     <a aria-label="{{cookiePolicyLink}}" class="cc-link" target="_blank" rel="noopener noreferrer" href="{{cookiePolicyHref}}">{{cookiePolicyLink}}</a>
    </span>
    <div class="cmplz-categories-wrap hide-content">
    <input type="checkbox" class="cc_chkbx" name="cc_Functional" value="cc_Functional" readonly checked>
    <label for="Functional" class="cc_chkbx_label">Functional</label>
    <input type="checkbox" class="cc_chkbx" name="Statistics" value="cc_Statistics">
    <label for="Statistics" class="cc_chkbx_label">Statistics</label>
    <input type="checkbox" class="cc_chkbx" name="Marketing" value="cc_Marketing">
    <label for="Marketing" class="cc_chkbx_label">Marketing</label>
    </div>
    `,
    buttons: `<div class="cc-compliance cc-highlight">
    <button aria-label="Accept cookies" class="cc-btn cc-accept-all cc-btn-no-href" (click)="delclineCookies()">Accept cookies</button>
     <button aria-label="show settings" class="cc-btn cc-save cc-show-settings cc-btn-no-href">Show settings</button>
    </div>`,
  },
  content: {
    message: "We use cookies to optimize our website and our service. ",

    cookiePolicyLink: "Cookie Policy",
    cookiePolicyHref: "https://www.holman.com/uk/wp-content/uploads/2025/04/cookie-policy-qms-gdpr-019-v2-0.pdf",
  },
};

@NgModule({
  declarations: [AppComponent],
  imports: [
    RouterModule,
    BrowserModule,
    IonicModule.forRoot({ innerHTMLTemplatesEnabled: true }),
    AppRoutingModule,
    ComponentsModule,
    TranslateModule.forRoot({
      loader: provideTranslateHttpLoader({
        prefix: './assets/i18n/',
        suffix: '.json'
      }),
      fallbackLang: 'en'
    }),
    NgcCookieConsentModule.forRoot(cookieConfig)
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initFactory,
      deps: [SqliteStorageService, AuthService, CoreService, ErrorLoggingService],
      multi: true
    },
    {
      provide: RouteReuseStrategy,
      useClass: IonicRouteStrategy
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RefreshInterceptor,
      multi: true
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    provideHttpClient(withInterceptorsFromDi()),
    CookieService
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
